import { useCallback, useEffect, useState } from "react";
import { parseISO } from "date-fns";
import { Lock, Unlock, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { DateCalendar } from "../booking/DateCalendar";
import { api, apiError } from "../../lib/api";
import { fieldCls, fmtDate } from "../../lib/pricing";

export const BlockedDates = ({ refreshKey }) => {
  const [blocked, setBlocked] = useState([]);
  const [booked, setBooked] = useState([]);
  const [day, setDay] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/blocked-dates");
      setBlocked(Array.isArray(data?.blocked) ? data.blocked : []);
      setBooked(Array.isArray(data?.booked) ? data.booked : []);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const isBlocked = blocked.some((b) => b.date === day);

  const toggle = async () => {
    try {
      if (isBlocked) {
        await api.delete(`/admin/blocked-dates/${day}`);
        toast.success(`Data ${fmtDate(day)} liberada`);
      } else {
        await api.post("/admin/blocked-dates", { date: day, reason });
        toast.success(`Data ${fmtDate(day)} bloqueada`);
      }
      setReason("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const unblock = async (d) => {
    try { await api.delete(`/admin/blocked-dates/${d}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <section data-testid="admin-blocked-dates" className="mt-14">
      <h2 className="font-display font-black uppercase text-2xl text-white">Agenda & bloqueios</h2>
      <p className="text-sm text-zinc-400 mt-1">Uma reserva por dia. Datas reservadas (vermelho) e bloqueadas (dourado) não aparecem para os clientes.</p>
      <div className="mt-6 grid lg:grid-cols-[auto_1fr] gap-8">
        <DateCalendar testid="admin-block-calendar" value={day} onChange={setDay} minOffset={0} disabledDates={booked}
          modifiers={{ blocked: blocked.map((b) => parseISO(b.date)), booked: booked.map((d) => parseISO(d)) }}
          modifiersClassNames={{ blocked: "!bg-[#FFD200]/25 !text-[#FFD200] font-bold", booked: "!bg-[#E8202A]/30 !text-red-200 !no-underline" }} />
        <div className="space-y-6">
          <div className="rounded-2xl border border-white/10 p-5 space-y-4">
            <p className="font-display font-bold text-lg text-white" data-testid="admin-block-selected">{day ? fmtDate(day) : "Selecione uma data no calend\u00e1rio"}</p>
            {day && !isBlocked && (
              <Input data-testid="admin-block-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (opcional), ex.: manutenção" className={fieldCls} />
            )}
            <button type="button" disabled={!day} onClick={toggle} data-testid="admin-block-toggle"
              className={`inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold transition-colors disabled:opacity-40 ${isBlocked ? "border border-white/20 text-white hover:border-[#FFD200]" : "bg-[#FFD200] text-[#08080C] hover:bg-white"}`}>
              {isBlocked ? <><Unlock className="w-4 h-4" /> Liberar data</> : <><Lock className="w-4 h-4" /> Bloquear data</>}
            </button>
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">Datas bloqueadas</p>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="admin-blocked-list">
              {blocked.length === 0 && <p className="text-sm text-zinc-400">Nenhuma data bloqueada.</p>}
              {blocked.map((b) => (
                <span key={b.date} className="inline-flex items-center gap-2 rounded-full border border-[#FFD200]/40 text-[#FFD200] pl-4 pr-1 py-1 text-sm">
                  {fmtDate(b.date)}{b.reason ? ` \u00b7 ${b.reason}` : ""}
                  <button onClick={() => unblock(b.date)} data-testid={`admin-unblock-${b.date}`} aria-label="Liberar" className="w-7 h-7 rounded-full grid place-items-center hover:bg-white/10"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
